import qs from 'qs'
import api from './api'

/**
 * CyberMatch API Service
 * Handles all CyberMatch-related API calls
 */

export interface UserTypeResponse {
  success: boolean
  userType: 'job_seeker' | 'employer' | 'admin' | null
  isJobSeeker: boolean
  isEmployer: boolean
  isAdmin: boolean
  company?: {
    id: number
    name: string
  }
}

export interface ProfileResponse {
  success: boolean
  profile: {
    email: string
    firstName: string
    lastName: string
    phone?: string
    city?: string
    province?: string
    country?: string
    bio?: string
    skillExtractionProgress: number
    resumeUploaded: boolean
    resumeUploadedAt?: string
  }
}

export interface StatusResponse {
  isSkillExtractionStarted: boolean
  isSkillExtractionComplete: boolean
  skillExtractionProgress: number
  isJobMatchingComplete: boolean
  matchingProgress: number
  unmatchedJobsCount?: number
  totalActiveJobs?: number
}

export interface UpdateProfileResponse {
  success: boolean
  message: string
  resumeUploaded: boolean
  skillExtractionStarted: boolean
  profile: ProfileResponse['profile']
}

export interface ResumeNameResponse {
  success: boolean
  fileName: string | null
  message?: string
}

export interface GenerateImageResponse {
  success: boolean
  message: string
  data: {
    s3Url: string
    originalUrl: string
    revisedPrompt: string
  }
}

export interface SkillsResponse {
  success: boolean
  skills: {
    hard: string[]
    soft: string[]
  }
}

export interface SoftSkillDetail {
  id: number
  name: string
  confidence: number
  knockriScore: number | null
  assessmentCount: number
  category: string
}

export interface SoftSkillsDetailResponse {
  success: boolean
  data: {
    skills: SoftSkillDetail[]
    totalAssessments: number
    confidencePercentage: number
    lastUpdated: string
  }
}

export interface JobSearchFilters {
  query?: string
  location?: string
  datePosted?: string
  employerId?: number
  page?: number
  perPage?: number
}

export interface MissingSoftSkill {
  rawName: string // job posting name e.g. "Communication"
  canonical: string // Knockri canonical name e.g. "Collaboration"
}

export interface GapSkill {
  name: string
  type: 'hard' | 'soft'
  gapStrength: number
  reason?: string
  knockriScore?: number
}

export interface RecommendedCourse {
  id: number
  title: string
  description?: string
  url?: string
  icon_url?: string
  provider?: string
  moodle_course_id?: number
  moodle_short_name?: string
  lti_enabled: boolean
  is_embeddable: boolean
  relevanceScore: number
  gapSkills: GapSkill[]
  totalCourseSkills: number
}

export interface RecommendedCoursesResponse {
  success: boolean
  courses: RecommendedCourse[]
  total: number
}

export interface CourseSkill {
  id: number
  name: string
  skillType: string
}

/** Public-facing course shape returned by the course endpoints. */
export interface CourseDTO {
  id: number
  title: string
  description?: string
  url?: string
  iconUrl?: string
  provider?: string
  moodleShortName?: string
  status: string
  isActive: boolean
  isEmbeddable: boolean
  ltiEnabled: boolean
  createdAt: string // serialized Date
  updatedAt: string // serialized Date
  skills: CourseSkill[]
}

export interface CoursesSearchResponse {
  success: boolean
  total: number
  page: number
  perPage: number
  courses: CourseDTO[]
}

export interface Job {
  id: number
  title: string
  summary: string
  description?: string
  location: string
  posted_at: string
  apply_to: string
  rating: number
  finalScore: number
  matched: boolean
  matchedSkills: string[]
  missingSkills: string[]
  // Split skill arrays (from backend breakdown)
  matchedHardSkills: string[]
  matchedSoftSkills: string[] // format: "SkillName (→Canonical: score/5)"
  missingHardSkills: string[]
  missingSoftSkills: MissingSoftSkill[]
  totalSkills: number
  breakdown?: {
    hardScore: number
    softScore: number
    hardWeight: number
    softWeight: number
  }
  employer: {
    id: number
    name: string
    logo: string | null
    website?: string
  }
  bookmarked: boolean
  applied: boolean
  fitLabel: {
    text: string
    subtext: string
  }
}

export interface JobSearchResponse {
  total: number
  page: number
  perPage: number
  jobs: Job[]
}

export interface Employer {
  id: number
  name: string
}

const CyberMatch = {
  /**
   * Get the current user's type (Job Seeker, Employer, Admin)
   * This is the core role detection function
   */
  getUserType: async (): Promise<UserTypeResponse> => {
    const response = await api.get('/api/cybermatch/user/type')
    return response.data
  },

  /**
   * Get job seeker profile data
   * SDF-Prototype: /api/job-seeker/details
   */
  getProfile: async (): Promise<ProfileResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/details')
    return response.data
  },

  /**
   * Get profile status for skill extraction and job matching progress
   * Used for polling during onboarding
   * SDF-Prototype: /api/job-seeker/status
   */
  getProfileStatus: async (): Promise<StatusResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/status')
    return response.data
  },

  /**
   * Update profile and optionally upload resume
   * @param formData - FormData containing file and profile fields
   * SDF-Prototype: /api/job-seeker/update-profile
   */
  updateProfile: async (formData: FormData): Promise<UpdateProfileResponse> => {
    const response = await api.post('/api/cybermatch/job-seeker/update-profile', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  /**
   * Get resume file name
   * SDF-Prototype: /api/job-seeker/resume/name
   */
  getResumeName: async (): Promise<ResumeNameResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/resume/name')
    return response.data
  },

  /**
   * Delete profile and all associated data (S3 files, skills, applications)
   * Use this to start fresh with profile creation
   */
  deleteProfile: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete('/api/cybermatch/job-seeker/profile')
    return response.data
  },

  /**
   * Get job seeker skills organized by hard/soft classification
   * SDF-Prototype: /api/job-seeker/skills
   */
  getSkills: async (): Promise<SkillsResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/skills')
    return response.data
  },

  /**
   * Generate test image using OpenAI DALL-E 3
   * Saves to S3 under user's username
   */
  generateTestImage: async (prompt: string, testName?: string): Promise<GenerateImageResponse> => {
    const response = await api.post('/api/test/openai/generate-image', {
      prompt,
      testName,
    })
    return response.data
  },

  /**
   * Search jobs with filters and matching scores
   * Returns jobs sorted by match score (best matches first)
   */
  searchJobs: async (filters: JobSearchFilters): Promise<JobSearchResponse> => {
    const response = await api.get('/api/cybermatch/jobs/search', {
      params: filters,
      paramsSerializer: params => qs.stringify(params, { skipNulls: true }),
    })
    return response.data
  },

  /**
   * Get job details by ID
   */
  getJobDetails: async (jobId: number): Promise<Job> => {
    const response = await api.get(`/api/cybermatch/jobs/${jobId}`)
    return response.data
  },

  /**
   * Toggle bookmark for a job
   */
  toggleBookmark: async (jobId: number, bookmark: boolean): Promise<void> => {
    await api.post(`/api/cybermatch/jobs/${jobId}/bookmark`, { bookmark })
  },

  /**
   * Apply to a job
   */
  applyToJob: async (jobId: number): Promise<void> => {
    await api.post(`/api/cybermatch/jobs/${jobId}/apply`)
  },

  /**
   * Get all employers (for search filter dropdown)
   */
  getEmployers: async (): Promise<Employer[]> => {
    const response = await api.get('/api/cybermatch/employers')
    return response.data.employers
  },

  /**
   * Get recommended jobs (high match scores >= 4.0)
   */
  getRecommendedJobs: async (): Promise<JobSearchResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/jobs/recommended')
    return {
      success: response.data.success,
      jobs: response.data.recommendedJobs || [],
      total: response.data.total || 0,
      page: 1,
      perPage: response.data.total || 0,
      totalPages: 1,
    }
  },

  /**
   * Get bookmarked jobs
   */
  getBookmarkedJobs: async (): Promise<JobSearchResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/jobs/bookmarked')
    return {
      success: response.data.success,
      jobs: response.data.bookmarkedJobs || [],
      total: response.data.total || 0,
      page: 1,
      perPage: response.data.total || 0,
      totalPages: 1,
    }
  },

  /**
   * Get applied jobs
   */
  getAppliedJobs: async (): Promise<JobSearchResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/jobs/applied')
    return {
      success: response.data.success,
      jobs: response.data.appliedJobs || [],
      total: response.data.total || 0,
      page: 1,
      perPage: response.data.total || 0,
      totalPages: 1,
    }
  },

  /**
   * Get archived jobs
   */
  getArchivedJobs: async (): Promise<JobSearchResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/jobs/archived')
    return {
      success: response.data.success,
      jobs: response.data.archivedJobs || [],
      total: response.data.total || 0,
      page: 1,
      perPage: response.data.total || 0,
      totalPages: 1,
    }
  },

  // ==========================================
  // ADMIN - Moodle Course Sync
  // ==========================================

  /**
   * Test Moodle API connection
   */
  testMoodleConnection: async () => {
    const response = await api.get('/api/cybermatch/admin/courses/test-moodle-connection')
    return response.data
  },

  /**
   * Sync courses from Moodle
   */
  syncCoursesFromMoodle: async (dryRun = false) => {
    const response = await api.post('/api/cybermatch/admin/courses/sync-from-moodle', { dryRun })
    return response.data
  },

  // ==========================================
  // KNOCKRI ASSESSMENT
  // ==========================================

  /**
   * Get Knockri assessment status
   */
  getKnockriAssessmentStatus: async () => {
    const response = await api.get('/api/cybermatch/knockri/assessment/status')
    return response.data
  },

  /**
   * Start Knockri assessment for a specific level
   */
  startKnockriAssessment: async (level: number) => {
    const response = await api.post('/api/cybermatch/knockri/assessment/start', { level })
    return response.data
  },

  /**
   * Get soft skills for the job seeker with confidence scores
   */
  getSoftSkills: async (): Promise<SoftSkillsDetailResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/skills/soft')
    return response.data
  },

  /**
   * Get soft skills for a specific applicant (employer view)
   */
  getApplicantSoftSkills: async (jobSeekerId: number): Promise<SoftSkillsDetailResponse> => {
    const response = await api.get(`/api/cybermatch/employer/applicants/${jobSeekerId}/soft-skills`)
    return response.data
  },

  /**
   * Get recommended courses for the job seeker based on skill gaps.
   * Soft skill gaps: Knockri score < 3/5 (or no assessment yet)
   * Hard skill gaps: Skills entirely absent from the seeker's profile
   */
  getRecommendedCourses: async (): Promise<RecommendedCoursesResponse> => {
    const response = await api.get('/api/cybermatch/job-seeker/courses/recommended')
    return response.data
  },

  /** Search the full course catalog. Returns the same item shape the UI renders. */
  searchCourses: async (): Promise<CoursesSearchResponse> => {
    const response = await api.get('/api/cybermatch/courses')
    return response.data
  },

  /**
   * Mark a course as completed and add its skills to the job seeker's profile
   * for job matching.
   */
  completeCourse: async (
    courseId: number,
  ): Promise<{
    success: boolean
    skillsAdded: string[]
    skillsUpdated: string[]
    message: string
  }> => {
    const response = await api.post(`/api/cybermatch/job-seeker/courses/${courseId}/complete`)
    return response.data
  },

  /** Mark a course as in-progress when the user opens it. */
  startCourse: async (courseId: number): Promise<{ success: boolean; status: string }> => {
    const response = await api.post(`/api/cybermatch/job-seeker/courses/${courseId}/start`)
    return response.data
  },

  /** Fetch all course progress records for the current job seeker. */
  getCourseProgress: async (): Promise<{
    success: boolean
    progress: Array<{
      courseId: number
      title: string
      status: 'not-started' | 'in-progress' | 'completed'
      started_at: string | null
      completed_at: string | null
    }>
  }> => {
    const response = await api.get('/api/cybermatch/job-seeker/courses/progress')
    return response.data
  },
}

export default CyberMatch
